import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import productService from '../services/productService';
import categoryService from '../services/categoryService';

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch products and categories from backend on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [productsData, categoriesData] = await Promise.all([
          productService.getAll({ available: true }),
          categoryService.getAll()
        ]);
        
        // Transform backend products to match frontend structure
        const transformedProducts = productsData.map(p => {
          const variants = (p.variants || [])
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(v => ({
              id: v.id,
              label: v.label,
              price: Number(v.pricePerUnit),
              stockQuantity: v.sellable ?? v.stockQuantity,
              isAvailable: v.isAvailable,
            }));
          const hasVariants = variants.length > 0;
          return {
            id: p.id,
            name: p.name,
            description: p.description,
            price: p.pricePerUnit || p.price_per_unit,
            weight: p.unitType || p.unit_type || 'Grams',
            category: p.categoryId || p.category_id,
            categoryName: p.category?.name,
            image: p.images?.[0]?.imageUrl || p.image || 'https://via.placeholder.com/200',
            inStock: p.isAvailable !== false && (
              hasVariants
                ? variants.some(v => v.isAvailable && v.stockQuantity > 0)
                : (p.stockQuantity || p.stock_quantity) > 0
            ),
            stockQuantity: p.stockQuantity || p.stock_quantity || 0,
            isAvailable: p.isAvailable ?? p.is_available ?? true,
            variants,
            shelfLifeDays: p.shelfLifeDays ?? p.shelf_life_days,
            keywords: p.keywords || '',
            hsnCode: p.hsnCode || p.hsn_code || '',
          };
        });

        // Add 'all' category at the beginning if not present.
        // Categories arrive from the backend already in admin-ranked order
        // (sortOrder ASC); keep that order and carry sortOrder through so the
        // admin can show/reorder ranks. The synthetic 'all' stays pinned first.
        const allCategories = [
          { id: 'all', name: 'All Products' },
          ...categoriesData.map(c => ({
            id: c.id,
            name: c.name,
            description: c.description,
            sortOrder: c.sortOrder,
            imageUrl: c.imageUrl || c.image_url || null,
          }))
        ];

        setProducts(transformedProducts);
        setCategories(allCategories);
      } catch (err) {
        console.error('Failed to fetch products/categories:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Refresh products from backend
  const refreshProducts = useCallback(async () => {
    try {
      const productsData = await productService.getAll({ available: true });
      const transformedProducts = productsData.map(p => {
        const variants = (p.variants || [])
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(v => ({ id: v.id, label: v.label, price: Number(v.pricePerUnit), stockQuantity: v.sellable ?? v.stockQuantity, isAvailable: v.isAvailable }));
        const hasVariants = variants.length > 0;
        return {
          id: p.id, name: p.name, description: p.description,
          price: p.pricePerUnit || p.price_per_unit,
          weight: p.unitType || p.unit_type || 'Grams',
          category: p.categoryId || p.category_id, categoryName: p.category?.name,
          image: p.images?.[0]?.imageUrl || p.image || 'https://via.placeholder.com/200',
          inStock: p.isAvailable !== false && (hasVariants ? variants.some(v => v.isAvailable && v.stockQuantity > 0) : (p.stockQuantity || p.stock_quantity) > 0),
          stockQuantity: p.stockQuantity || p.stock_quantity || 0,
          isAvailable: p.isAvailable ?? p.is_available ?? true,
          variants,
          shelfLifeDays: p.shelfLifeDays ?? p.shelf_life_days,
          keywords: p.keywords || '',
          hsnCode: p.hsnCode || p.hsn_code || '',
        };
      });
      setProducts(transformedProducts);
    } catch (err) {
      console.error('Failed to refresh products:', err);
    }
  }, []);

  // Refresh categories from backend
  const refreshCategories = useCallback(async () => {
    try {
      const categoriesData = await categoryService.getAll();
      const allCategories = [
        { id: 'all', name: 'All Products' },
        ...categoriesData.map(c => ({
          id: c.id,
          name: c.name,
          description: c.description,
          sortOrder: c.sortOrder,
          imageUrl: c.imageUrl || c.image_url || null,
        }))
      ];
      setCategories(allCategories);
    } catch (err) {
      console.error('Failed to refresh categories:', err);
    }
  }, []);

  // Persist an admin drag-and-drop reorder (BMF-84). Optimistically re-orders
  // the context list (so the shop reflects it immediately), then saves and
  // re-syncs with the server. On failure, the refresh reverts to server truth.
  const reorderCategories = useCallback(async (orderedIds) => {
    const orderedIdSet = new Set(orderedIds); // O(1) membership for the trailing filter
    setCategories(prev => {
      const all = prev.filter(c => c.id === 'all');
      const rest = prev.filter(c => c.id !== 'all');
      const byId = new Map(rest.map(c => [c.id, c]));
      const ranked = orderedIds.map((id, i) => {
        const c = byId.get(id);
        return c ? { ...c, sortOrder: i + 1 } : null;
      }).filter(Boolean);
      // Anything not in orderedIds (e.g. Uncategorized) keeps its place at the end.
      const trailing = rest.filter(c => !orderedIdSet.has(c.id));
      return [...all, ...ranked, ...trailing];
    });
    try {
      await categoryService.reorder(orderedIds);
    } catch (err) {
      console.error('Failed to reorder categories:', err);
      throw err;
    } finally {
      // Re-sync with server truth on both success and failure (revert on error).
      await refreshCategories();
    }
  }, [refreshCategories]);

  // Category management functions - now use backend API
  const addCategory = useCallback(async (category) => {
    try {
      const newCategory = await categoryService.create({
        name: category.name,
        description: category.description || '',
        imageUrl: category.imageUrl || null,
      });
      await refreshCategories();
      return newCategory;
    } catch (err) {
      console.error('Failed to add category:', err);
      throw err;
    }
  }, [refreshCategories]);

  const uploadCategoryImage = useCallback(async (id, file) => {
    if (!id) {
      throw new Error('Category ID is required');
    }
    if (!file) {
      throw new Error('Please select an image');
    }
    try {
      const res = await categoryService.uploadImage(id, file);
      if (res && res.success === false) {
        throw new Error(res.error || 'Failed to upload category image');
      }
      await refreshCategories();
      return res;
    } catch (err) {
      console.error('Failed to upload category image:', err);
      throw err;
    }
  }, [refreshCategories]);

  const updateCategory = useCallback(async (id, updates) => {
    try {
      await categoryService.update(id, updates);
      await refreshCategories();
    } catch (err) {
      console.error('Failed to update category:', err);
      throw err;
    }
  }, [refreshCategories]);

  const deleteCategory = useCallback(async (id) => {
    // Don't allow deleting 'all' category
    if (id === 'all') return false;
    try {
      await categoryService.delete(id);
      await refreshCategories();
      return true;
    } catch (err) {
      console.error('Failed to delete category:', err);
      throw err;
    }
  }, [refreshCategories]);

  const getCategoryById = useCallback((id) => {
    return categories.find(c => c.id === id || c.id === Number(id));
  }, [categories]);

  // Product management functions - now use backend API
  const addProduct = useCallback(async (product) => {
    try {
      // Ensure categoryId is a number
      const categoryId = Number(product.categoryId || product.category);
      if (!categoryId || isNaN(categoryId)) {
        throw new Error('Please select a valid category');
      }

      // Unit type is validated server-side against the admin-managed unit_types
      // table now (BMF-87), so pass the selected label straight through.
      const unitType = product.unitType;

      const newProduct = await productService.create({
        name: product.name,
        description: product.description,
        categoryId: categoryId,
        pricePerUnit: product.price || product.pricePerUnit,
        unitType: unitType,
        stockQuantity: product.stockQuantity ?? 25,
        shelfLifeDays: product.shelfLifeDays ?? 3,
        isAvailable: product.isAvailable !== false,
        image: product.image || product.imageUrl || '',
        keywords: product.keywords ?? null,
        hsnCode: product.hsnCode || null,
        taxMasterId: product.taxMasterId ?? null,
      });
      await refreshProducts();
      return newProduct;
    } catch (err) {
      console.error('Failed to add product:', err);
      throw err;
    }
  }, [refreshProducts]);

  const updateProduct = useCallback(async (id, updates) => {
    try {
      // Ensure categoryId is a number if provided
      const categoryId = updates.categoryId || updates.category;

      // Unit type validated server-side (BMF-87); pass through when provided.
      const unitType = updates.unitType || undefined;

      await productService.update(id, {
        name: updates.name,
        description: updates.description,
        categoryId: categoryId ? Number(categoryId) : undefined,
        pricePerUnit: updates.price || updates.pricePerUnit,
        unitType: unitType,
        stockQuantity: updates.stockQuantity,
        shelfLifeDays: updates.shelfLifeDays,
        isAvailable: updates.isAvailable,
        image: updates.image || updates.imageUrl || '',
        keywords: updates.keywords ?? undefined,
        hsnCode: updates.hsnCode ?? undefined,
        // BMF-137: send taxMasterId whenever the key is present (null clears it).
        taxMasterId: 'taxMasterId' in updates ? updates.taxMasterId : undefined,
      });
      await refreshProducts();
    } catch (err) {
      console.error('Failed to update product:', err);
      throw err;
    }
  }, [refreshProducts]);

  const toggleStock = useCallback(async (id) => {
    try {
      await productService.updateStock(id, 0);
      await refreshProducts();
    } catch (err) {
      console.error('Failed to update stock:', err);
      throw err;
    }
  }, [refreshProducts]);

  // Update stock quantity
  const updateStock = useCallback(async (id, quantity) => {
    try {
      await productService.updateStock(id, quantity);
      await refreshProducts();
    } catch (err) {
      console.error('Failed to update stock:', err);
      throw err;
    }
  }, [refreshProducts]);

  // Decrease stock when item is ordered (local only - backend handles this via orders)
  const decreaseStock = useCallback((id, decreaseBy = 1) => {
    setProducts(prev =>
      prev.map(p => {
        if (p.id === id) {
          const newQty = Math.max(0, (p.stockQuantity || 0) - decreaseBy);
          return { ...p, stockQuantity: newQty, inStock: newQty > 0 };
        }
        return p;
      })
    );
  }, []);

  const getProductsByCategory = useCallback((categoryId) => {
    if (categoryId === 'all' || !categoryId) return products;
    // Support both numeric IDs and string IDs
    return products.filter(p => 
      p.category === categoryId || 
      p.category === Number(categoryId) ||
      String(p.category) === String(categoryId)
    );
  }, [products]);

  const getProductById = useCallback((id) => {
    return products.find(p => p.id === id || p.id === Number(id));
  }, [products]);

  const value = useMemo(() => ({
    products,
    categories,
    loading,
    error,
    addProduct,
    updateProduct,
    toggleStock,
    updateStock,
    decreaseStock,
    getProductsByCategory,
    getProductById,
    addCategory,
    uploadCategoryImage,
    updateCategory,
    deleteCategory,
    reorderCategories,
    getCategoryById,
    refreshProducts,
    refreshCategories
  }), [products, categories, loading, error, addProduct, updateProduct, toggleStock, updateStock, decreaseStock, getProductsByCategory, getProductById, addCategory, uploadCategoryImage, updateCategory, deleteCategory, reorderCategories, getCategoryById, refreshProducts, refreshCategories]);

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

ProductProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default ProductContext;